import React, { useEffect, useRef, useState } from "react";

export default function ValentineRunaway() {
  const containerRef = useRef(null);
  const noBtnRef = useRef(null);

  const [accepted, setAccepted] = useState(false);
  const [noTextIndex, setNoTextIndex] = useState(0);

  // ✅ Demo-only: replace with your own code (do NOT commit real codes to a public repo)
  const STARBUCKS_GIFT_CODE = "XXXX-XXXX-XXXX-XXXX";

  const noTexts = [
    "No 💔",
    "Are you sure? 😳",
    "Think again 😤",
    "Not happening 😌",
    "Nice try 😈",
    "Nope 🙅‍♂️",
    "Stop chasing me 😭",
  ];

  const [noPos, setNoPos] = useState({ x: 220, y: 220 });

  useEffect(() => {
    const placeInitial = () => {
      const c = containerRef.current;
      const b = noBtnRef.current;
      if (!c || !b) return;

      const cw = c.clientWidth;
      const ch = c.clientHeight;
      const bw = b.offsetWidth;
      const bh = b.offsetHeight;

      const x = Math.max(12, Math.min(cw - bw - 12, Math.floor(cw * 0.62)));
      const y = Math.max(12, Math.min(ch - bh - 12, Math.floor(ch * 0.55)));
      setNoPos({ x, y });
    };

    placeInitial();
    window.addEventListener("resize", placeInitial);
    return () => window.removeEventListener("resize", placeInitial);
  }, []);

  const moveNoButton = () => {
    const c = containerRef.current;
    const b = noBtnRef.current;
    if (!c || !b) return;

    const cw = c.clientWidth;
    const ch = c.clientHeight;
    const bw = b.offsetWidth;
    const bh = b.offsetHeight;

    const padding = 12;
    const maxX = Math.max(padding, cw - bw - padding);
    const maxY = Math.max(padding, ch - bh - padding);

    const x = Math.floor(Math.random() * (maxX - padding + 1)) + padding;
    const y = Math.floor(Math.random() * (maxY - padding + 1)) + padding;

    setNoPos({ x, y });
    setNoTextIndex((i) => (i + 1) % noTexts.length);
  };

  const onNoHover = () => {
    if (accepted) return;
    moveNoButton();
  };

  const onNoTouch = (e) => {
    if (accepted) return;
    e.preventDefault();
    moveNoButton();
  };

  const onYes = () => setAccepted(true);

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "grid",
        placeItems: "center",
        background:
          "radial-gradient(circle at top, rgba(255, 182, 193, 0.55), rgba(255,255,255,1))",
        fontFamily:
          'ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, "Helvetica Neue", Arial',
        padding: 16,
      }}
    >
      <div
        ref={containerRef}
        style={{
          width: "min(720px, 95vw)",
          height: "min(520px, 80vh)",
          background: "rgba(255,255,255,0.85)",
          border: "1px solid rgba(0,0,0,0.08)",
          borderRadius: 24,
          boxShadow: "0 20px 60px rgba(0,0,0,0.12)",
          position: "relative",
          overflow: "hidden",
          padding: 22,
        }}
      >
        {!accepted && (
          <>
            <FloatingHearts />
            <div style={{ textAlign: "center", marginTop: 30 }}>
              <div style={{ fontSize: 48, lineHeight: 1 }}>💘</div>
              <h1 style={{ margin: "14px 0 8px", fontSize: 36 }}>
                Will you be my Valentine?
              </h1>
              <p style={{ margin: 0, opacity: 0.75 }}>
                Choose wisely. The “No” button is… shy.
              </p>
            </div>
          </>
        )}

        {accepted ? (
          <AcceptedView giftCode={STARBUCKS_GIFT_CODE} />
        ) : (
          <>
            <div
              style={{
                position: "absolute",
                left: "50%",
                transform: "translateX(-50%)",
                bottom: 42,
                display: "flex",
                gap: 14,
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <button
                onClick={onYes}
                style={{
                  border: "none",
                  borderRadius: 999,
                  padding: "14px 22px",
                  fontSize: 18,
                  fontWeight: 700,
                  cursor: "pointer",
                  background: "#ff4d6d",
                  color: "white",
                  boxShadow: "0 10px 24px rgba(255,77,109,0.35)",
                }}
              >
                Yes 💕
              </button>

              <span style={{ fontSize: 14, opacity: 0.6 }}>
                (Try clicking No 😈)
              </span>
            </div>

            <button
              ref={noBtnRef}
              onMouseEnter={onNoHover}
              onMouseMove={onNoHover}
              onTouchStart={onNoTouch}
              onPointerDown={onNoTouch}
              style={{
                position: "absolute",
                left: noPos.x,
                top: noPos.y,
                border: "1px solid rgba(0,0,0,0.12)",
                borderRadius: 999,
                padding: "12px 18px",
                fontSize: 16,
                fontWeight: 700,
                cursor: "pointer",
                background: "white",
                color: "#111",
                boxShadow: "0 10px 24px rgba(0,0,0,0.10)",
                transition: "left 140ms ease, top 140ms ease, transform 120ms ease",
                userSelect: "none",
                touchAction: "none",
              }}
              aria-label="No button that moves away"
              title="You can't catch me 😈"
            >
              {noTexts[noTextIndex]}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

function AcceptedView({ giftCode }) {
  const confetti = Array.from({ length: 60 }, (_, i) => i);
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(giftCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch {
      // clipboard can fail on some browsers if not HTTPS
    }
  };

  return (
    <div
      style={{
        height: "100%",
        display: "grid",
        placeItems: "center",
        textAlign: "center",
        padding: 22,
        position: "relative",
      }}
    >
      {confetti.map((i) => (
        <span
          key={i}
          style={{
            position: "absolute",
            top: -20,
            left: `${Math.random() * 100}%`,
            fontSize: 18 + Math.random() * 18,
            opacity: 0.9,
            animation: `fall ${1200 + Math.random() * 1400}ms linear ${
              Math.random() * 400
            }ms infinite`,
            transform: `rotate(${Math.random() * 180}deg)`,
          }}
        >
          {["💖", "💘", "💕", "✨", "🌸"][Math.floor(Math.random() * 5)]}
        </span>
      ))}

      <div style={{ zIndex: 2, width: "min(520px, 92%)" }}>
        <div style={{ fontSize: 56, lineHeight: 1 }}>🎉</div>
        <h2 style={{ margin: "14px 0 10px", fontSize: 34 }}>
          Yessss! You’re my Valentine 💕
        </h2>
        <p style={{ margin: "0 0 16px", opacity: 0.75, fontSize: 18 }}>
          Correct answer detected. System locked. 😌
        </p>

        <div
          style={{
            border: "1px dashed rgba(0,0,0,0.18)",
            borderRadius: 18,
            padding: 14,
            background: "rgba(255,255,255,0.9)",
          }}
        >
          <div style={{ fontWeight: 800, marginBottom: 6 }}>
            Starbucks Gift Code (demo)
          </div>
          <div
            style={{
              fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas",
              fontSize: 18,
              letterSpacing: 1,
            }}
          >
            {giftCode}
          </div>

          <button
            onClick={copy}
            style={{
              marginTop: 10,
              border: "1px solid rgba(0,0,0,0.12)",
              borderRadius: 999,
              padding: "10px 14px",
              fontWeight: 800,
              cursor: "pointer",
              background: "white",
            }}
          >
            {copied ? "Copied ✅" : "Copy code"}
          </button>

          <div style={{ marginTop: 10, fontSize: 13, opacity: 0.6 }}>
            Don’t commit real gift codes in public repos—use a backend.
          </div>
        </div>

        <div style={{ marginTop: 14, fontSize: 14, opacity: 0.6 }}>
          Refresh the page to try again.
        </div>
      </div>

      <style>{`
        @keyframes fall {
          0% { transform: translateY(-20px) rotate(0deg); opacity: 0; }
          10% { opacity: 1; }
          100% { transform: translateY(620px) rotate(240deg); opacity: 0.9; }
        }
      `}</style>
    </div>
  );
}

function FloatingHearts() {
  const hearts = Array.from({ length: 18 }, (_, i) => i);
  return (
    <>
      {hearts.map((i) => (
        <span
          key={i}
          style={{
            position: "absolute",
            left: `${Math.random() * 100}%`,
            top: `${Math.random() * 100}%`,
            fontSize: 16 + Math.random() * 18,
            opacity: 0.18,
            animation: `floaty ${3500 + Math.random() * 3500}ms ease-in-out ${
              Math.random() * 800
            }ms infinite`,
            userSelect: "none",
            pointerEvents: "none",
          }}
        >
          {["💗", "💞", "💖"][Math.floor(Math.random() * 3)]}
        </span>
      ))}

      <style>{`
        @keyframes floaty {
          0% { transform: translateY(0px); }
          50% { transform: translateY(-16px); }
          100% { transform: translateY(0px); }
        }
      `}</style>
    </>
  );
}
